import { useEffect, useState } from "react";
import api, { apiErr, fmtDate, API_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MODULE_LABELS } from "@/lib/constants";
import { PageHeader, Loader, EmptyState } from "@/components/ui-kit";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { LifeBuoy, Plus, Paperclip, Send, Eye, Loader2, Download, Trash2, Star } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["Nouveau", "En cours", "Traité", "Fermé"];
const PRIORITIES = ["Basse", "Moyenne", "Haute"];
const PRIO_CLASS = { Haute: "bg-rose-100 text-rose-700 border-rose-200", Moyenne: "bg-amber-100 text-amber-700 border-amber-200", Basse: "bg-slate-100 text-slate-600 border-slate-200" };
const MODULE_OPTS = Object.entries(MODULE_LABELS);
const ageDays = (iso) => Math.max(0, Math.floor((Date.now() - new Date(iso)) / 86400000));

function StatCard({ label, value, alert, testid }) {
  return (
    <div className={`rounded-xl border p-4 ${alert ? "border-rose-300 bg-rose-50" : "bg-white"}`} data-testid={testid}>
      <p className="text-xs text-slate-500">{label}</p>
      <p className={`text-2xl font-bold ${alert ? "text-rose-600" : "text-brand-700"}`}>{value}</p>
    </div>
  );
}

export default function Support() {
  const { role, user } = useAuth();
  const isAdmin = ["super_admin", "direction_generale"].includes(role);
  const [items, setItems] = useState(null);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);
  const [detail, setDetail] = useState(null);
  const [comment, setComment] = useState("");
  const [fStatut, setFStatut] = useState("all");
  const [fModule, setFModule] = useState("all");

  const loadStats = () => { if (isAdmin) api.get("/tickets/stats").then((r) => setStats(r.data)).catch(() => {}); };
  const load = () => { api.get("/tickets").then((r) => setItems(r.data)); loadStats(); };
  useEffect(() => {
    load();
    if (isAdmin) api.get("/users").then((r) => setUsers(r.data.filter((u) => u.active !== false))).catch(() => {});
  }, [isAdmin]);

  const openNew = () => { setForm({ sujet: "", module: "", priorite: "Moyenne", description: "", attachments: [] }); setOpen(true); };
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const upload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try {
      const { data } = await api.post("/tickets/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("attachments", [...(form.attachments || []), data]); toast.success("Pièce jointe ajoutée");
    } catch (err) { toast.error(apiErr(err)); }
    e.target.value = "";
  };

  const save = async () => {
    if (!form.sujet.trim() || !form.description.trim()) { toast.error("Sujet et description requis"); return; }
    setSaving(true);
    try { await api.post("/tickets", form); toast.success("Ticket créé"); setOpen(false); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  const openDetail = async (t) => { try { const { data } = await api.get(`/tickets/${t.id}`); setDetail(data); setComment(""); } catch (e) { toast.error(apiErr(e)); } };
  const refresh = async (id) => { const { data } = await api.get(`/tickets/${id}`); setDetail(data); load(); };
  const changeStatus = async (statut) => { try { await api.patch(`/tickets/${detail.id}/status`, { statut }); toast.success("Statut mis à jour"); refresh(detail.id); } catch (e) { toast.error(apiErr(e)); } };
  const changePriority = async (priorite) => { try { await api.patch(`/tickets/${detail.id}/priority`, { priorite }); toast.success("Priorité mise à jour"); refresh(detail.id); } catch (e) { toast.error(apiErr(e)); } };
  const assign = async (assigned_to) => { try { await api.patch(`/tickets/${detail.id}/assign`, { assigned_to }); toast.success("Ticket attribué"); refresh(detail.id); } catch (e) { toast.error(apiErr(e)); } };
  const addComment = async () => { if (!comment.trim()) return; try { await api.post(`/tickets/${detail.id}/comment`, { message: comment }); setComment(""); refresh(detail.id); } catch (e) { toast.error(apiErr(e)); } };
  const dlAtt = (idx, nom) => { const tok = localStorage.getItem("omnibat_token"); fetch(`${API_BASE}/tickets/${detail.id}/attachment/${idx}`, { headers: { Authorization: `Bearer ${tok}` } }).then((r) => { if (!r.ok) throw new Error(); return r.blob(); }).then((b) => window.open(URL.createObjectURL(b))).catch(() => toast.error("Impossible d'ouvrir la pièce jointe")); };
  const removeTicket = async () => { if (!window.confirm(`Supprimer définitivement le ticket ${detail.numero} ?`)) return; try { await api.delete(`/tickets/${detail.id}`); toast.success("Ticket supprimé"); setDetail(null); load(); } catch (e) { toast.error(apiErr(e)); } };
  const rate = async (note) => { try { await api.post(`/tickets/${detail.id}/rate`, { note }); toast.success("Merci pour votre retour !"); refresh(detail.id); } catch (e) { toast.error(apiErr(e)); } };

  const filtered = (items || []).filter((t) => (fStatut === "all" || t.statut === fStatut) && (fModule === "all" || t.module === fModule));

  return (
    <div>
      <PageHeader title="Support" subtitle="Signalez un dysfonctionnement et suivez vos tickets">
        <Button onClick={openNew} data-testid="add-ticket" className="bg-brand-600 hover:bg-brand-700 text-white"><Plus className="h-4 w-4 mr-1.5" />Nouveau ticket</Button>
      </PageHeader>

      {isAdmin && stats && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3" data-testid="support-dashboard">
            <StatCard label="Tickets ouverts" value={stats.open_total} />
            <StatCard label="Hautes non traitées" value={stats.haute_open} alert={stats.haute_open > 0} testid="stat-haute-open" />
            <StatCard label="Âge moyen (ouverts)" value={`${stats.avg_open_days} j`} />
            <StatCard label="Satisfaction moy." value={stats.avg_satisfaction ? `${stats.avg_satisfaction} / 5` : "—"} testid="stat-satisfaction" />
          </div>
          <div className="flex flex-wrap gap-2 mb-4 text-xs">
            {STATUSES.map((s) => <span key={s} className="px-2 py-1 rounded border bg-white">{s} : <b>{stats.by_statut[s] || 0}</b></span>)}
            <span className="px-2 py-1 rounded border bg-white">Plus ancien ouvert : <b>{stats.oldest_open_days} j</b></span>
          </div>
        </>
      )}

      {items === null ? <Loader /> : items.length === 0 ? (
        <EmptyState icon={LifeBuoy} title="Aucun ticket" subtitle="Créez un ticket pour signaler un problème rencontré dans l'application." />
      ) : (
        <div>
          {isAdmin && (
            <div className="flex flex-wrap gap-2 mb-3">
              <Select value={fStatut} onValueChange={setFStatut}>
                <SelectTrigger className="w-44 bg-white" data-testid="filter-statut"><SelectValue placeholder="Statut" /></SelectTrigger>
                <SelectContent><SelectItem value="all">Tous les statuts</SelectItem>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={fModule} onValueChange={setFModule}>
                <SelectTrigger className="w-52 bg-white" data-testid="filter-module"><SelectValue placeholder="Module" /></SelectTrigger>
                <SelectContent className="max-h-72"><SelectItem value="all">Tous les modules</SelectItem>{MODULE_OPTS.map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
              {(fStatut !== "all" || fModule !== "all") && <Button variant="ghost" size="sm" onClick={() => { setFStatut("all"); setFModule("all"); }} data-testid="filter-reset">Réinitialiser</Button>}
            </div>
          )}
          <div className="rounded-xl border bg-white overflow-hidden"><div className="overflow-x-auto scrollbar-thin">
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead>N°</TableHead><TableHead>Sujet</TableHead><TableHead>Module</TableHead>
              {isAdmin && <TableHead>Demandeur</TableHead>}<TableHead>Affecté à</TableHead>
              <TableHead>Priorité</TableHead><TableHead>Statut</TableHead><TableHead>Âge</TableHead><TableHead>Date</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {filtered.length === 0 ? <TableRow><TableCell colSpan={isAdmin ? 10 : 9} className="text-center text-slate-400 py-6">Aucun ticket ne correspond aux filtres</TableCell></TableRow> : filtered.map((t) => {
                const urgent = t.priorite === "Haute" && ["Nouveau", "En cours"].includes(t.statut);
                return (
                <TableRow key={t.id} data-testid={`ticket-row-${t.id}`} className={`hover:bg-slate-50 cursor-pointer ${urgent ? "bg-rose-50/60" : ""}`} onClick={() => openDetail(t)}>
                  <TableCell className="font-mono text-xs">{t.numero}</TableCell>
                  <TableCell className="font-semibold max-w-[220px] truncate">{t.sujet}</TableCell>
                  <TableCell className="text-slate-600 text-sm">{MODULE_LABELS[t.module] || t.module || "—"}</TableCell>
                  {isAdmin && <TableCell className="text-slate-600 text-sm">{t.created_by_name}</TableCell>}
                  <TableCell className="text-slate-600 text-sm">{t.assigned_to_name || "—"}</TableCell>
                  <TableCell><span className={`px-2 py-0.5 rounded text-[11px] border ${PRIO_CLASS[t.priorite] || PRIO_CLASS.Moyenne}`}>{t.priorite || "Moyenne"}</span></TableCell>
                  <TableCell><StatusBadge value={t.statut} /></TableCell>
                  <TableCell className={`text-xs ${urgent ? "text-rose-600 font-semibold" : "text-slate-500"}`}>{ageDays(t.created_at)} j</TableCell>
                  <TableCell className="text-xs text-slate-500">{fmtDate(t.created_at)}</TableCell>
                  <TableCell><Button variant="ghost" size="icon" data-testid={`ticket-view-${t.id}`} onClick={(e) => { e.stopPropagation(); openDetail(t); }}><Eye className="h-4 w-4" /></Button></TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
          </div></div>
        </div>
      )}

      {/* Création */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="w-[95vw] max-w-lg max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-head">Nouveau ticket de support</DialogTitle></DialogHeader>
          {form && (
            <div className="space-y-3">
              <div><Label className="text-xs">Sujet</Label><Input value={form.sujet} onChange={(e) => set("sujet", e.target.value)} placeholder="Ex : Erreur à l'enregistrement d'une facture" className="mt-1" data-testid="ticket-sujet" /></div>
              <div><Label className="text-xs">Module concerné</Label>
                <Select value={form.module || "none"} onValueChange={(v) => set("module", v === "none" ? "" : v)}>
                  <SelectTrigger className="mt-1" data-testid="ticket-module"><SelectValue placeholder="Choisir un module…" /></SelectTrigger>
                  <SelectContent className="max-h-72"><SelectItem value="none">— Général —</SelectItem>{MODULE_OPTS.map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Priorité</Label>
                <Select value={form.priorite || "Moyenne"} onValueChange={(v) => set("priorite", v)}>
                  <SelectTrigger className="mt-1" data-testid="ticket-priorite"><SelectValue /></SelectTrigger>
                  <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Description du problème</Label><Textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={4} placeholder="Décrivez le problème, les étapes pour le reproduire…" className="mt-1" data-testid="ticket-description" /></div>
              <div>
                <Label className="text-xs">Pièce jointe / capture d'écran (optionnel)</Label>
                <div className="mt-1 flex items-center gap-2 flex-wrap">
                  <label className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border cursor-pointer hover:bg-slate-50" data-testid="ticket-attach-label">
                    <Paperclip className="h-4 w-4" />Ajouter un fichier
                    <input type="file" className="hidden" onChange={upload} data-testid="ticket-attach-input" />
                  </label>
                  {(form.attachments || []).map((a, i) => (
                    <span key={i} className="text-xs bg-slate-100 rounded px-2 py-1">{a.nom} ({a.taille})</span>
                  ))}
                </div>
              </div>
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
                <Button disabled={saving} onClick={save} data-testid="ticket-submit" className="bg-brand-600 hover:bg-brand-700 text-white">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Créer le ticket"}</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Détail */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="w-[95vw] max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="ticket-detail-dialog">
          {detail && (
            <>
              <DialogHeader><DialogTitle className="font-head flex flex-wrap items-center gap-2"><span className="font-mono text-sm text-slate-500">{detail.numero}</span>{detail.sujet}</DialogTitle></DialogHeader>
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <StatusBadge value={detail.statut} />
                <span className={`px-2 py-0.5 rounded text-xs border ${PRIO_CLASS[detail.priorite] || PRIO_CLASS.Moyenne}`} data-testid="detail-priorite-badge">Priorité {detail.priorite || "Moyenne"}</span>
                {detail.module && <span className="rounded bg-slate-100 px-2 py-0.5 text-xs">{MODULE_LABELS[detail.module] || detail.module}</span>}
                <span className="text-xs text-slate-500">Par {detail.created_by_name} · {fmtDate(detail.created_at)}</span>
              </div>
              <p className="text-sm whitespace-pre-line bg-slate-50 rounded-lg border p-3">{detail.description}</p>

              {(detail.attachments || []).length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {detail.attachments.map((a, i) => (
                    <button key={i} onClick={() => dlAtt(i, a.nom)} data-testid={`ticket-att-${i}`} className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded border hover:bg-slate-50"><Download className="h-3.5 w-3.5" />{a.nom}</button>
                  ))}
                </div>
              )}

              {isAdmin && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 rounded-lg border p-3 bg-brand-50/40">
                  <div><Label className="text-xs">Attribuer à</Label>
                    <Select value={detail.assigned_to || "none"} onValueChange={(v) => v !== "none" && assign(v)}>
                      <SelectTrigger className="mt-1 bg-white" data-testid="ticket-assign"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                      <SelectContent className="max-h-72"><SelectItem value="none">— Non attribué —</SelectItem>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-xs">Priorité</Label>
                    <Select value={detail.priorite || "Moyenne"} onValueChange={changePriority}>
                      <SelectTrigger className="mt-1 bg-white" data-testid="ticket-priority"><SelectValue /></SelectTrigger>
                      <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-xs">Statut</Label>
                    <Select value={detail.statut} onValueChange={changeStatus}>
                      <SelectTrigger className="mt-1 bg-white" data-testid="ticket-status"><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              <div>
                <p className="text-xs font-mono uppercase text-slate-400 mb-1">Échanges</p>
                <div className="space-y-2 max-h-48 overflow-y-auto scrollbar-thin">
                  {(detail.comments || []).length === 0 ? <p className="text-xs text-slate-400">Aucune réponse pour le moment.</p> :
                    detail.comments.map((c) => (
                      <div key={c.id} className={`rounded-lg border p-2 text-sm ${c.is_admin ? "bg-brand-50 border-brand-200" : "bg-white"}`} data-testid={`ticket-comment-${c.id}`}>
                        <div className="flex justify-between text-[11px] text-slate-500 mb-0.5"><span className="font-semibold">{c.by}{c.is_admin && " · Support"}</span><span>{fmtDate(c.date)}</span></div>
                        <p className="whitespace-pre-line">{c.message}</p>
                      </div>
                    ))}
                </div>
                <div className="flex gap-2 mt-2">
                  <Textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={2} placeholder="Écrire une réponse…" data-testid="ticket-comment-input" />
                  <Button onClick={addComment} data-testid="ticket-comment-send" className="bg-brand-600 hover:bg-brand-700 text-white self-end"><Send className="h-4 w-4" /></Button>
                </div>
              </div>

              <div>
                <p className="text-xs font-mono uppercase text-slate-400 mb-1">Historique des traitements</p>
                <div className="rounded-lg border overflow-hidden">
                  <table className="w-full text-xs"><tbody>
                    {(detail.history || []).map((h, i) => (
                      <tr key={i} className="border-t"><td className="p-1.5">{h.action}</td><td className="p-1.5 text-slate-500">{h.by}</td><td className="p-1.5 text-right text-slate-400">{fmtDate(h.date)}</td></tr>
                    ))}
                  </tbody></table>
                </div>
              </div>

              {detail.statut === "Fermé" && (
                <div className="rounded-lg border p-3 bg-amber-50/50" data-testid="ticket-satisfaction">
                  <p className="text-xs font-semibold text-slate-600 mb-1.5">Satisfaction</p>
                  {detail.satisfaction ? (
                    <div className="flex items-center gap-1">
                      {[1, 2, 3, 4, 5].map((n) => <Star key={n} className={`h-5 w-5 ${n <= detail.satisfaction ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />)}
                      <span className="ml-2 text-sm text-slate-600">{detail.satisfaction}/5</span>
                    </div>
                  ) : user?.id === detail.created_by ? (
                    <div className="flex items-center gap-1">
                      {[1, 2, 3, 4, 5].map((n) => <button key={n} onClick={() => rate(n)} data-testid={`rate-${n}`} className="text-slate-300 hover:text-amber-400 transition-colors"><Star className="h-6 w-6" /></button>)}
                      <span className="ml-2 text-xs text-slate-500">Notez la résolution de votre ticket</span>
                    </div>
                  ) : <p className="text-xs text-slate-400">En attente de la note du demandeur.</p>}
                </div>
              )}

              {isAdmin && (
                <div className="flex justify-end pt-1">
                  <Button variant="outline" onClick={removeTicket} data-testid="ticket-delete" className="text-rose-600 border-rose-200 hover:bg-rose-50"><Trash2 className="h-4 w-4 mr-1.5" />Supprimer le ticket</Button>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
